import React, { useState } from 'react';

/**
 * InsightsPanel — Executive-style visualization for structured LLM reports
 * 
 * Detects and renders ALL structured sections as executive cards:
 *   - Key Metrics Overview (pipe-separated tables)
 *   - Key Findings tables
 *   - Business Review sections (## "Title")
 *   - Business Insights
 *   - Observations
 *   - Recommended Actions (green executive bullets)
 *   - Business Implications (priority-based)
 * 
 * Renders them as management-ready visual reports instead of plain markdown text.
 */

// Helper: strip all markdown formatting from a string
const cleanText = (s) => {
  if (!s) return '';
  return s
    .replace(/\*\*/g, '')      // bold markers
    .replace(/\*/g, '')        // italic markers
    .replace(/`/g, '')         // code ticks
    .replace(/["""]/g, '')     // smart and straight quotes
    .replace(/^[-•:]+\s*/, '') // leading bullets/colons
    .replace(/:\s*$/, '')      // trailing colons
    .trim();
};

// Severity detection based on keywords
const getSeverity = (text, value) => {
  const t = ((text || '') + ' ' + (value || '')).toLowerCase();
  
  // Critical / Red
  if (t.includes('critical') || t.includes('overdue') || t.includes('flagged') || 
      t.includes('not yet') || t.includes('no budget') || t.includes('inadequate') ||
      t.includes('violation') || t.includes('risk') || t.includes('alert') ||
      t.includes('not available') || t.includes('missing') || t.includes('poor') ||
      t.includes('low contract') || t.includes('🔴') || t.includes('❌')) {
    return { level: 'critical', color: '#C62828', bg: '#FFEBEE', border: '#EF5350', icon: '🔴', label: 'Critical' };
  }
  
  // Warning / Amber
  if (t.includes('warning') || t.includes('moderate') || t.includes('between') || 
      t.includes('may be') || t.includes('limited') || t.includes('small') ||
      t.includes('watch') || t.includes('monitor') || t.includes('pending') ||
      t.includes('attention') || t.includes('mid-stage') ||
      t.includes('concentrated') || t.includes('⚠') || t.includes('🟡') ||
      t.includes('observation')) {
    return { level: 'warning', color: '#E65100', bg: '#FFF3E0', border: '#FF9800', icon: '🟡', label: 'Watch' };
  }
  
  // Good / Green
  if (t.includes('strong') || t.includes('good') || t.includes('healthy') || 
      t.includes('compliant') || t.includes('active') || t.includes('aligned') ||
      t.includes('adequate') || t.includes('positive') || t.includes('minimal') ||
      t.includes('fair') || t.includes('on track') || t.includes('✅') || t.includes('🟢') ||
      t.includes('insight') || t.includes('strength')) {
    return { level: 'good', color: '#2E7D32', bg: '#E8F5E9', border: '#4CAF50', icon: '🟢', label: 'Good' };
  }
  
  // Neutral / Info
  return { level: 'info', color: '#1565C0', bg: '#E3F2FD', border: '#42A5F5', icon: '🔵', label: 'Info' };
};

// Parse any pipe-separated table (Key Metrics Overview, Key Findings, etc.)
const parseTable = (text) => {
  const findings = [];
  const lines = text.split('\n');
  let inTable = false;
  let headerPassed = false;
  let headers = [];
  
  for (const line of lines) {
    // Detect table header (has pipes and contains metric/value/status/name/category keywords)
    if (!inTable && line.includes('|')) {
      const lower = line.toLowerCase();
      if (lower.includes('metric') || lower.includes('value') || lower.includes('status') ||
          lower.includes('finding') || lower.includes('category') || lower.includes('name') ||
          lower.includes('item') || lower.includes('kpi')) {
        inTable = true;
        headers = line.split('|').map(c => cleanText(c)).filter(c => c.length > 0);
        continue;
      }
    }
    
    // Separator row
    if (inTable && line.match(/^\s*\|[\s\-:|]+\|?\s*$/)) {
      headerPassed = true;
      continue;
    }
    
    // Data row
    if (inTable && headerPassed && line.includes('|')) {
      const cells = line.split('|').map(c => cleanText(c)).filter(c => c.length > 0);
      if (cells.length >= 2) {
        findings.push({
          metric: cells[0],
          value: cells[1],
          interpretation: cells.length >= 3 ? cells[2] : ''
        });
      }
      continue;
    }
    
    // End of table
    if (inTable && headerPassed && !line.includes('|') && line.trim().length > 0) {
      break;
    }
  }
  
  return findings;
};

// Parse ALL markdown sections (## "Title" with content) into executive cards
// Now handles ANY section, including "Business Insights", "Observations", etc.
const parseAllSections = (text) => {
  const sections = [];
  const lines = text.split('\n');
  
  let currentSection = null;
  
  // Comprehensive header regex: matches ## "title", ## **title**, ## title:, ### title
  // Handles emojis, bold markers, straight and smart quotes
  const headerRegex = /^#{2,3}\s+(?:[🟢🔴🟡⚠️✅❌📊📈💼🔥⚡💡📋]\s*)?(?:\*\*)?["""]?(.+?)["""]?(?:\*\*)?:?\s*$/;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headerMatch = line.match(headerRegex);
    
    if (headerMatch) {
      const title = cleanText(headerMatch[1]);
      
      // Skip if empty or generic
      if (!title || title.length < 2) continue;
      
      // Skip sections handled separately (Key Findings/Metrics tables handled by parseTable)
      const lowerTitle = title.toLowerCase();
      if (lowerTitle.includes('key metrics overview') || 
          lowerTitle.includes('key findings') ||
          lowerTitle === 'summary' ||
          lowerTitle.startsWith('data source')) {
        if (currentSection && (currentSection.items.length > 0 || currentSection.description)) {
          sections.push(currentSection);
        }
        currentSection = null;
        continue;
      }
      
      // Save previous section if it has content
      if (currentSection && (currentSection.items.length > 0 || currentSection.description)) {
        sections.push(currentSection);
      }
      
      currentSection = { title, items: [], description: '', rawText: '' };
      continue;
    }
    
    if (!currentSection) continue;
    
    // Skip separator/end markers
    if (line.trim() === '---' || line.trim() === '') continue;
    
    // Bullet points (-, •, *, numbered 1.)
    const bulletMatch = line.match(/^\s*[-•*]\s+(.+)$/);
    const numberedMatch = line.match(/^\s*\d+\.\s+(.+)$/);
    const match = bulletMatch || numberedMatch;
    
    if (match) {
      const item = cleanText(match[1]);
      if (item) {
        currentSection.items.push(item);
        currentSection.rawText += ' ' + item;
      }
      continue;
    }
    
    // Sub-bullet (indented with more spaces)
    const subBulletMatch = line.match(/^\s{2,}[-•*]\s+(.+)$/);
    if (subBulletMatch && currentSection.items.length > 0) {
      const item = cleanText(subBulletMatch[1]);
      // Append as continuation to last item
      currentSection.items[currentSection.items.length - 1] += ` → ${item}`;
      continue;
    }
    
    // Plain text line — add to description
    if (line.trim().length > 0 && !line.startsWith('#')) {
      const text = cleanText(line);
      if (text) {
        currentSection.description += (currentSection.description ? ' ' : '') + text;
        currentSection.rawText += ' ' + text;
      }
    }
  }
  
  // Push final section
  if (currentSection && (currentSection.items.length > 0 || currentSection.description)) {
    sections.push(currentSection);
  }
  
  return sections;
};

// Extract metric value from bullet text (e.g., "Total Tenders Posted: 10" → {label, value})
const extractMetricValue = (text) => {
  // Pattern: "Label": value  OR  Label: value  OR  Label — value
  const match = text.match(/^["""]?(.+?)["""]?\s*[:=—–]\s*(.+)$/);
  if (match) {
    return { label: cleanText(match[1]), value: cleanText(match[2]) };
  }
  return null;
};

// Section icon based on title keywords
const getSectionIcon = (title) => {
  const t = title.toLowerCase();
  if (t.includes('tender') || t.includes('bid')) return '📋';
  if (t.includes('contract')) return '📝';
  if (t.includes('vendor') || t.includes('supplier')) return '🏢';
  if (t.includes('risk')) return '⚠️';
  if (t.includes('budget') || t.includes('financial') || t.includes('revenue')) return '💰';
  if (t.includes('compliance') || t.includes('audit')) return '🛡️';
  if (t.includes('performance')) return '📈';
  if (t.includes('schedule') || t.includes('timeline')) return '📅';
  if (t.includes('staff') || t.includes('team') || t.includes('resource')) return '👥';
  if (t.includes('customer') || t.includes('client')) return '🤝';
  if (t.includes('inventory') || t.includes('stock')) return '📦';
  if (t.includes('delivery') || t.includes('logistics')) return '🚚';
  if (t.includes('insight')) return '💡';
  if (t.includes('observation')) return '👁️';
  if (t.includes('recommend') || t.includes('action')) return '✅';
  if (t.includes('implication')) return '💼';
  if (t.includes('strength')) return '🟢';
  if (t.includes('attention') || t.includes('concern')) return '⚠️';
  return '📊';
};

// Section card with auto-detected metrics vs bullets
const SectionCard = ({ section, index }) => {
  const [expanded, setExpanded] = useState(true);
  const severity = getSeverity(section.rawText, section.title);
  
  // Try to extract metrics from items (label: value pattern)
  const metrics = section.items.map(item => extractMetricValue(item)).filter(Boolean);
  const hasMetrics = metrics.length > 0 && metrics.length >= section.items.length * 0.5; // at least 50% metrics
  
  const icon = getSectionIcon(section.title);
  
  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      border: `1px solid ${severity.border}`,
      borderLeft: `5px solid ${severity.border}`,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
    >
      {/* Header */}
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px',
          background: severity.bg, cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${severity.border}30` : 'none'
        }}
      >
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>{section.title}</div>
          <div style={{ fontSize: '10px', color: severity.color, fontWeight: 600, marginTop: '2px' }}>
            {section.items.length} data point{section.items.length !== 1 ? 's' : ''}
          </div>
        </div>
        <span style={{
          fontSize: '9px', fontWeight: 700, padding: '3px 8px', borderRadius: '10px',
          background: severity.color, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px'
        }}>
          {severity.icon} {severity.label}
        </span>
        <span style={{ fontSize: '14px', color: '#999', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
      </div>
      
      {/* Body */}
      {expanded && (
        <div style={{ padding: '14px 16px' }}>
          {section.description && (
            <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.5, marginBottom: '10px', fontStyle: 'italic' }}>
              {section.description}
            </div>
          )}
          
          {hasMetrics ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              {metrics.map((metric, i) => {
                const metricSeverity = getSeverity(metric.value, metric.label);
                return (
                  <div key={i} style={{
                    padding: '10px 14px', background: '#fafafa', borderRadius: '8px',
                    border: '1px solid #eee', borderLeft: `3px solid ${metricSeverity.border}`
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', fontWeight: 600, marginBottom: '4px' }}>
                      {metric.label}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: metricSeverity.color }}>
                      {metric.value}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : section.items.length > 0 ? (
            <div>
              {section.items.map((item, i) => {
                const itemSeverity = getSeverity(item);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '8px 12px', background: i % 2 === 0 ? '#fafafa' : '#fff',
                    borderRadius: '6px', marginBottom: '4px', fontSize: '12px',
                    color: '#444', lineHeight: 1.5,
                    borderLeft: `3px solid ${itemSeverity.border}`
                  }}>
                    <span style={{ color: itemSeverity.color, fontWeight: 700, flexShrink: 0, fontSize: '14px' }}>
                      {itemSeverity.icon}
                    </span>
                    <span>{item}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Executive-style Key Metrics table (polished, color-coded)
const KeyMetricsTable = ({ findings }) => {
  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #e0e0e0',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)' }}>
            <th style={{ padding: '12px 16px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: '12px', letterSpacing: '0.5px' }}>
              METRIC
            </th>
            <th style={{ padding: '12px 16px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: '12px', letterSpacing: '0.5px' }}>
              VALUE
            </th>
            <th style={{ padding: '12px 16px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: '12px', letterSpacing: '0.5px' }}>
              STATUS / INTERPRETATION
            </th>
          </tr>
        </thead>
        <tbody>
          {findings.map((finding, i) => {
            const severity = getSeverity(finding.interpretation, finding.value);
            return (
              <tr key={i} style={{
                background: i % 2 === 0 ? '#fff' : '#fafafa',
                borderLeft: `4px solid ${severity.border}`,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = severity.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'; }}
              >
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#333' }}>
                  {finding.metric}
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                  <span style={{
                    display: 'inline-block', padding: '4px 12px', borderRadius: '6px',
                    background: severity.bg, color: severity.color, fontWeight: 700, fontSize: '14px'
                  }}>
                    {finding.value}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', color: '#555', fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>{severity.icon}</span>
                  {finding.interpretation}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Main InsightsPanel Component
 */
const InsightsPanel = ({ answer }) => {
  if (!answer || typeof answer !== 'string') return null;
  
  const lowerAnswer = answer.toLowerCase();
  
  // Detect what's present
  const hasTable = /\|.*\|/.test(answer) && 
    (lowerAnswer.includes('metric') || lowerAnswer.includes('finding') || lowerAnswer.includes('value'));
  const hasMarkdownSections = /^#{2,3}\s+/m.test(answer);
  
  if (!hasTable && !hasMarkdownSections) return null;
  
  const tableFindings = hasTable ? parseTable(answer) : [];
  const allSections = hasMarkdownSections ? parseAllSections(answer) : [];
  
  if (tableFindings.length === 0 && allSections.length === 0) return null;
  
  // Determine table title
  const metricsTitle = lowerAnswer.includes('key metrics overview') ? 'Key Metrics Overview' :
                       lowerAnswer.includes('key metrics') ? 'Key Metrics' :
                       lowerAnswer.includes('key finding') ? 'Key Findings' :
                       'Metrics Overview';
  
  return (
    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
      {/* Key Metrics Overview — Executive Table */}
      {tableFindings.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px',
            paddingBottom: '10px', borderBottom: '2px solid #1565C0'
          }}>
            <span style={{ fontSize: '20px' }}>📊</span>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1565C0' }}>{metricsTitle}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                Executive Summary — {tableFindings.length} metric{tableFindings.length !== 1 ? 's' : ''} analyzed
              </div>
            </div>
          </div>
          <KeyMetricsTable findings={tableFindings} />
        </div>
      )}
      
      {/* All Markdown Sections as Executive Cards (Business Review, Insights, Observations, Recommended Actions, etc.) */}
      {allSections.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px',
            paddingBottom: '10px', borderBottom: '2px solid #1565C0'
          }}>
            <span style={{ fontSize: '20px' }}>📈</span>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1565C0' }}>Executive Dashboard</div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {allSections.length} section{allSections.length !== 1 ? 's' : ''} — Management Report
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px' }}>
            {allSections.map((section, i) => (
              <SectionCard key={i} section={section} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Detect if the answer has structured content that InsightsPanel will render.
 * Used by parent components to decide whether to hide/move the raw text display.
 */
export const hasStructuredInsights = (answer) => {
  if (!answer || typeof answer !== 'string') return false;
  const hasTable = /\|.*\|/.test(answer) && 
    (answer.toLowerCase().includes('metric') || answer.toLowerCase().includes('finding') || answer.toLowerCase().includes('value'));
  const hasMarkdownSections = /^#{2,3}\s+/m.test(answer);
  return hasTable || hasMarkdownSections;
};

export default InsightsPanel;
